import { z } from "zod";

/** Parse a complete, unambiguous decimal amount; never discard a sign or exponent. */
export function parseMinorUnits(raw: string, exponent: number): number | null {
  const value = raw.trim();
  if (!/^(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d+)?$/.test(value)) return null;
  if ((value.split(".")[1]?.length ?? 0) > exponent) return null;
  const minor = Math.round(Number(value.replaceAll(",", "")) * 10 ** exponent);
  return Number.isSafeInteger(minor) && minor >= 0 ? minor : null;
}

/**
 * ISO 4217 currencies the app can record gifts in. `exponent` is the number
 * of minor-unit digits (JPY has none — ¥1 is the smallest unit). All money
 * is stored in minor units (cents, pence, yen) as integers.
 */
export const CURRENCIES = {
  USD: { name: "US Dollar", symbol: "$", exponent: 2 },
  EUR: { name: "Euro", symbol: "€", exponent: 2 },
  GBP: { name: "British Pound", symbol: "£", exponent: 2 },
  CAD: { name: "Canadian Dollar", symbol: "CA$", exponent: 2 },
  AUD: { name: "Australian Dollar", symbol: "A$", exponent: 2 },
  NZD: { name: "New Zealand Dollar", symbol: "NZ$", exponent: 2 },
  JPY: { name: "Japanese Yen", symbol: "¥", exponent: 0 },
  CHF: { name: "Swiss Franc", symbol: "CHF", exponent: 2 },
  SEK: { name: "Swedish Krona", symbol: "kr", exponent: 2 },
  NOK: { name: "Norwegian Krone", symbol: "kr", exponent: 2 },
  DKK: { name: "Danish Krone", symbol: "kr", exponent: 2 },
  ISK: { name: "Icelandic Króna", symbol: "kr", exponent: 0 },
  PLN: { name: "Polish Złoty", symbol: "zł", exponent: 2 },
  CZK: { name: "Czech Koruna", symbol: "Kč", exponent: 2 },
  HUF: { name: "Hungarian Forint", symbol: "Ft", exponent: 2 },
  RON: { name: "Romanian Leu", symbol: "lei", exponent: 2 },
  BGN: { name: "Bulgarian Lev", symbol: "лв", exponent: 2 },
  ILS: { name: "Israeli New Shekel", symbol: "₪", exponent: 2 },
  ZAR: { name: "South African Rand", symbol: "R", exponent: 2 },
  KES: { name: "Kenyan Shilling", symbol: "KSh", exponent: 2 },
  NGN: { name: "Nigerian Naira", symbol: "₦", exponent: 2 },
  GHS: { name: "Ghanaian Cedi", symbol: "GH₵", exponent: 2 },
  INR: { name: "Indian Rupee", symbol: "₹", exponent: 2 },
  SGD: { name: "Singapore Dollar", symbol: "S$", exponent: 2 },
  HKD: { name: "Hong Kong Dollar", symbol: "HK$", exponent: 2 },
  TWD: { name: "New Taiwan Dollar", symbol: "NT$", exponent: 2 },
  KRW: { name: "South Korean Won", symbol: "₩", exponent: 0 },
  CNY: { name: "Chinese Yuan", symbol: "CN¥", exponent: 2 },
  THB: { name: "Thai Baht", symbol: "฿", exponent: 2 },
  PHP: { name: "Philippine Peso", symbol: "₱", exponent: 2 },
  IDR: { name: "Indonesian Rupiah", symbol: "Rp", exponent: 2 },
  MYR: { name: "Malaysian Ringgit", symbol: "RM", exponent: 2 },
  VND: { name: "Vietnamese Đồng", symbol: "₫", exponent: 0 },
  AED: { name: "UAE Dirham", symbol: "د.إ", exponent: 2 },
  SAR: { name: "Saudi Riyal", symbol: "﷼", exponent: 2 },
  TRY: { name: "Turkish Lira", symbol: "₺", exponent: 2 },
  MXN: { name: "Mexican Peso", symbol: "MX$", exponent: 2 },
  BRL: { name: "Brazilian Real", symbol: "R$", exponent: 2 },
  ARS: { name: "Argentine Peso", symbol: "AR$", exponent: 2 },
  CLP: { name: "Chilean Peso", symbol: "CL$", exponent: 0 },
  COP: { name: "Colombian Peso", symbol: "CO$", exponent: 2 },
  PEN: { name: "Peruvian Sol", symbol: "S/", exponent: 2 },
} as const;

export type CurrencyCode = keyof typeof CURRENCIES;
export const CURRENCY_CODES = Object.keys(CURRENCIES) as [CurrencyCode, ...CurrencyCode[]];
export const zCurrencyCode = z.enum(CURRENCY_CODES);

/** exchange rates are stored as integer micro-units: 1_000_000 = 1.0 */
export const FX_RATE_SCALE = 1_000_000;
export const zFxRateMicros = z.number().int().min(1).max(1_000_000_000_000);

/** convert an original-currency amount (minor units) to base minor units */
export function convertToBase(originalMinor: number, fxRateMicros: number): number {
  return Math.round((originalMinor * fxRateMicros) / FX_RATE_SCALE);
}

/**
 * HMRC Gift Aid benefit limits: a donation only qualifies when the value of
 * any benefits given to the donor stays within these thresholds.
 * Amounts in pence.
 */
export function giftAidBenefitLimit(donationPence: number): number {
  if (donationPence <= 100_00) return Math.floor(donationPence * 0.25);
  if (donationPence <= 1_000_00) return 25_00;
  return Math.min(Math.floor(donationPence * 0.05), 2_500_00);
}

/** basic-rate relief: 25p per £1 while the basic income-tax rate is 20% */
export const GIFT_AID_RATE_BP = 2500;
export function giftAidClaimAmount(donationPence: number): number {
  return Math.floor((donationPence * GIFT_AID_RATE_BP) / 10_000);
}
